import React from "react";

import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import Languages from "../../translations";

const SUMMARY_BG_COLOR = "#9ce2f5";
const SUMMARY_TEXT_COLOR = "#424242";
const DETAILS_TEXT_COLOR = "#424242";

const GeneralInfo = ({ data }) => {
  if (!data) return null;

  const summarySx = {
    backgroundColor: SUMMARY_BG_COLOR,
    color: SUMMARY_TEXT_COLOR,
    "& .MuiAccordionSummary-expandIconWrapper": {
      color: SUMMARY_TEXT_COLOR,
    },
  };

  const detailsSx = {
    backgroundColor: "white",
    color: DETAILS_TEXT_COLOR,
    "& .MuiTableCell-root": {
      color: DETAILS_TEXT_COLOR,
    },
    "& .MuiTypography-root": {
      color: DETAILS_TEXT_COLOR,
    },
  };

  return (
    <div>
      {/* Basic Info - Always Shown */}
      <Accordion sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
          <Typography fontWeight="bold" sx={{ color: SUMMARY_TEXT_COLOR }}>
            {Languages.Preinformation1}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={detailsSx}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell>
                  <strong>{Languages.child}</strong>
                </TableCell>
                <TableCell>{data.ChildName}</TableCell>
              </TableRow>
              {data.ChildBirthDate && (
                <TableRow>
                  <TableCell>
                    <strong>{Languages.childsBirthdate}</strong>
                  </TableCell>
                  <TableCell>{data.ChildBirthDate}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell>
                  <strong>{Languages.ci_CentreName}</strong>
                </TableCell>
                <TableCell>{data.CentreName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>{Languages.bbGroup}</strong>
                </TableCell>
                <TableCell>{data.GroupName}</TableCell>
              </TableRow>
              {data.Guardian1Name && (
                <TableRow>
                  <TableCell>
                    <strong>{Languages.guardian} 1</strong>
                  </TableCell>
                  <TableCell>{data.Guardian1Name}</TableCell>
                </TableRow>
              )}
              {data.Guardian2Name && (
                <TableRow>
                  <TableCell>
                    <strong>{Languages.guardian} 2</strong>
                  </TableCell>
                  <TableCell>{data.Guardian2Name}</TableCell>
                </TableRow>
              )}
              {data.EceTeacherName && (
                <TableRow>
                  <TableCell>
                    <strong>{Languages.eceTeacher}</strong>
                  </TableCell>
                  <TableCell>{data.EceTeacherName}</TableCell>
                </TableRow>
              )}
              {data.PsTeacherName && (
                <TableRow>
                  <TableCell>
                    <strong>{Languages.EarlyEduTeacher}</strong>
                  </TableCell>
                  <TableCell>{data.PsTeacherName}</TableCell>
                </TableRow>
              )}
              {data.OtherPersonnel && (
                <TableRow>
                  <TableCell>
                    <strong>{Languages.otherParticipants}</strong>
                  </TableCell>
                  <TableCell>{data.OtherPersonnel}</TableCell>
                </TableRow>
              )}
              {data.ChildrenTalkMethodUsed != null && (
                <TableRow>
                  <TableCell>
                    <strong>
                      {Languages.ChildrenTalkMethodUsed1}{" "}
                      {Languages.ChildrenTalkMethodUsed2}
                    </strong>
                  </TableCell>
                  <TableCell>
                    {data.ChildrenTalkMethodUsed
                      ? Languages.permYes
                      : Languages.permNo}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>

      {/* Group VASU - Always shown if available */}
      {(data.GroupVasuObjectives || data.GroupVasuCriteria) && (
        <Accordion sx={{ mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
            <Typography fontWeight="bold" sx={{ color: SUMMARY_TEXT_COLOR }}>
              {Languages.groupVasu}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={detailsSx}>
            {data.GroupVasuValues && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {Languages.vasuValues}
                </Typography>
                <Typography variant="body2">{data.GroupVasuValues}</Typography>
              </Box>
            )}
            {data.GroupVasuObjectives && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {Languages.groupObjectives}
                </Typography>
                <Typography variant="body2">
                  {data.GroupVasuObjectives}
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Preschool arrangements — LEOPS only */}
      {data.IsLeopsActive &&
        (data.GroupVasuPreschoolText1 || data.GroupVasuPreschoolText2) && (
          <Accordion sx={{ mt: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={summarySx}>
              <Typography fontWeight="bold" sx={{ color: SUMMARY_TEXT_COLOR }}>
                {Languages.GroupSupportAndArrangements}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={detailsSx}>
              {data.GroupVasuPreschoolText1 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {Languages.GroupSupports}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {data.GroupVasuPreschoolText1}
                  </Typography>
                </Box>
              )}
              {data.GroupVasuPreschoolText2 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {Languages.GroupSpecificSupport}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {data.GroupVasuPreschoolText2}
                  </Typography>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}
    </div>
  );
};

export default GeneralInfo;
